// «Yaxınlıqda başqa repetitorlar» на профиле /tutors/<slug> (28.10.2026).
//
// Кого показываем: тот же вид (репетитор / кружок), хотя бы один общий предмет,
// не сама анкета. Расстояние — только между точками на карте (building с
// координатами; у анкеты без точки — её станция метро). Рядом = до NEAR_KM.
// Рядом никого — того же предмета в том же городе (онлайн-анкета — среди всех),
// без расстояния: сначала с общим главным предметом, потом с фото, потом по имени.
// Чистая функция — проверка в scripts/verify-tutor-profile.mts.
import { BAKU_CITY, METRO } from "@/data/map-taxonomy";
import type { PublicPartner } from "@/lib/map-public.functions";

export const NEARBY_MAX = 4;
export const NEAR_KM = 5;

type Spot = { lat: number; lng: number };

export type NearbyRow = { p: PublicPartner; km: number | null };
export type Nearby = { mode: "near" | "city"; rows: NearbyRow[] };

/** Расстояние по прямой, км (гаверсинус). */
export function kmBetween(a: Spot, b: Spot): number {
  const rad = (x: number) => (x * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Точка, от которой меряем: своя точка (building), иначе метро (только Баку). Онлайн — нет. */
export function spotOf(p: PublicPartner): Spot | null {
  if (p.address_mode === "online") return null;
  if (p.address_mode === "building" && p.lat !== null && p.lng !== null) return { lat: p.lat, lng: p.lng };
  const city = p.city || BAKU_CITY;
  const m = p.metro && city === BAKU_CITY ? METRO.find((x) => x.code === p.metro) : undefined;
  return m ? { lat: m.lat, lng: m.lng } : null;
}

const cityOf = (p: PublicPartner) => (p.address_mode === "online" ? null : p.city || BAKU_CITY);

export function nearbyPartners(me: PublicPartner, rows: readonly PublicPartner[]): Nearby {
  const main = me.subjects[0];
  const same = rows.filter((r) => r.id !== me.id && r.kind === me.kind && !r.is_paused && r.subjects.some((s) => me.subjects.includes(s)));
  const from = spotOf(me);
  if (from) {
    const near = same
      .filter((r) => r.address_mode === "building" && r.lat !== null && r.lng !== null)
      .map((r) => ({ p: r, km: kmBetween(from, { lat: r.lat!, lng: r.lng! }) }))
      .filter((x) => x.km <= NEAR_KM)
      .sort((a, b) => a.km - b.km || a.p.name.localeCompare(b.p.name))
      .slice(0, NEARBY_MAX);
    if (near.length) return { mode: "near", rows: near };
  }
  const city = cityOf(me);
  const inCity = same
    .filter((r) => city === null || cityOf(r) === city || r.address_mode === "online")
    .sort(
      (a, b) =>
        Number(b.subjects.includes(main)) - Number(a.subjects.includes(main)) ||
        Number(!!b.photo_url) - Number(!!a.photo_url) ||
        a.name.localeCompare(b.name),
    )
    .slice(0, NEARBY_MAX)
    .map((p) => ({ p, km: null }));
  return { mode: "city", rows: inCity };
}

/** В данные loader — без «о себе» и ориентира: карточке они не нужны. */
export const slimNearby = (n: Nearby): Nearby => ({ ...n, rows: n.rows.map(({ p, km }) => ({ p: { ...p, about: null, landmark: null }, km })) });
